import { ActionModal } from "@/shared/base/components/ActionModal/ActionModal";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { useToggle } from "@/shared/base/hooks/useToggle";
import {
  Box,
  ButtonProps,
  Divider,
  Link,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { createState } from "@/global/store/zustand_store";
import React from "react";
import { throttleTime } from "@/global/utils/utils";
import {
  NotificationModal,
  NotificationModalProps,
} from "./InfoPopup/NotificationModal";

export const useGenericModal = createState({
  isOpen: false,
  isClosing: false,
  title: "" as string | React.ReactNode,
  renderHeader: null as (() => React.ReactNode) | null,
  description: "" as string | React.ReactNode,
  titleIconVariant: "" as "error" | "warning" | "info" | "success",
  hideTitleIcon: false,
  confirmButtonText: "" as string | React.ReactNode,
  confirmButtonProps: {} as ButtonProps,
  closeButtonText: "" as string | React.ReactNode,
  isCloseButtonHidden: false,
  isConfirmButtonHidden: false,
  closeButtonProps: {} as ButtonProps,
  onClickClose: (() => {}) as () => void,
  onClickConfirm: (() => {}) as () => void,
  onClickConfirmWithoutModalClose: null as (() => void) | null,
  isCloseButtonDisabled: false,
  isConfirmButtonLoading: false,

  closeIconProps: {} as NotificationModalProps["closeIconProps"],
});

export function GenericModal() {
  const props = useGenericModal();

  const onCloseModal = async () => {
    props.set({ isClosing: true });
    await props?.onClickClose?.();
    props.set({ isOpen: false });
    throttleTime(400, () => {
      props.reset();
    });
  };

  const onConfirmModal = async () => {
    if (props?.onClickConfirmWithoutModalClose) {
      return await props?.onClickConfirmWithoutModalClose?.();
    }

    props.set({ isClosing: true });
    await props?.onClickConfirm?.();
    props.set({ isOpen: false });
    throttleTime(400, () => {
      props.reset();
    });
  };

  return (
    <NotificationModal
      isOpen={props.isOpen}
      onClose={onCloseModal}
      titleIconVariant={props.titleIconVariant || "info"}
      hideTitleIcon={props?.hideTitleIcon}
      title={props?.title || <Box height={24} />}
      renderHeader={props?.renderHeader || undefined}
      closeIconProps={props?.closeIconProps}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      bodyProps={{
        hideTopDivider: true,
        hideBottomDivider: true,
      }}
      renderBody={() => {
        return (
          <Box>
            <Divider />
            <Box p={3}>{props?.description}</Box>
            <Divider />
          </Box>
        );
      }}
      renderFooter={() => {
        return (
          <Stack
            p={2}
            direction={"row"}
            justifyContent={"flex-end"}
            spacing={2}
          >
            {!props?.isCloseButtonHidden ? (
              <ModalActionButton
                isDisabled={props?.isCloseButtonDisabled}
                variant="primary"
                onClick={onCloseModal}
                style={{ minWidth: "120px" }}
                {...(props?.closeButtonProps as any)}
              >
                {props?.closeButtonText || "Close"}
              </ModalActionButton>
            ) : null}

            {!props?.isConfirmButtonHidden ? (
              <ModalActionButton
                isLoading={props?.isConfirmButtonLoading || props?.isClosing}
                isDisabled={props?.isConfirmButtonLoading || props?.isClosing}
                variant="primary"
                onClick={onConfirmModal}
                style={{ minWidth: "120px" }}
                {...(props?.confirmButtonProps as any)}
              >
                {props?.confirmButtonText || "Confirm"}
              </ModalActionButton>
            ) : null}
          </Stack>
        );
      }}
    />
  );
}
